import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { bookingService, assetService } from '../services/api';
import { OPERATING_HOURS } from '../utils/constants';
import './BookingModal.css';

function BookingModal({ mode, booking, assets = [], allBookings = {}, user, isMinimized, onClose, onMinimize, onExpand, onUpdate }) {
  // Compare user IDs as strings to handle ObjectId vs string comparison
  const bookingUserId = booking?.extendedProps?.userId?.toString();
  const currentUserId = user?.id?.toString();
  const isAdminOrSelf = (user?.role == 'admin') || (user?.id?.toString() == booking?.extendedProps?.userId?.toString());
  const isReadOnly = (mode === 'edit' && !isAdminOrSelf) || mode === 'view';
  // Extract operating hours from constants
  const {
    OPEN_HOUR,
    CLOSE_HOUR,
    LAST_START_HOUR,
    LAST_START_MINUTE,
    MINUTE_INCREMENTS,
    MIN_BOOKING_DURATION
  } = OPERATING_HOURS;
  
  // Categorize assets by type
  const getAssetOptions = (type) => {
    // Get active assets of this type (isDeleted filter is already applied by the API)
    const activeAssets = assets.filter(a => a.type === type);
    
    // If we're in view mode and the booking references a deleted asset, include it
    if (mode === 'view' && booking?.extendedProps) {
      const assetIdField = `${type}Id`;
      const assetNameField = `${type}Name`;
      const assetDeletedField = `${type}Deleted`;
      
      const referencedAssetId = booking.extendedProps[assetIdField];
      const referencedAssetName = booking.extendedProps[assetNameField];
      const isReferencedAssetDeleted = booking.extendedProps[assetDeletedField];
      
      // If the referenced asset is deleted and not in active assets, create a virtual asset entry
      if (referencedAssetId && isReferencedAssetDeleted && 
          !activeAssets.find(a => a._id === referencedAssetId)) {
        const deletedAssetName = referencedAssetName || `Deleted ${type}`;
        const deletedAsset = {
          _id: referencedAssetId,
          name: `${deletedAssetName} - deleted`,
          type: type,
          isDeleted: true
        };
        return [...activeAssets, deletedAsset];
      }
      
      // Handle case where asset is stored as an object in extendedProps
      const assetObj = booking.extendedProps[assetIdField];
      if (assetObj && typeof assetObj === 'object' && assetObj.isDeleted && 
          !activeAssets.find(a => a._id === assetObj._id)) {
        const deletedAsset = {
          ...assetObj,
          name: `${assetObj.name} - deleted`
        };
        return [...activeAssets, deletedAsset];
      }
    }
    
    return activeAssets;
  };
  
  const wingOptions = getAssetOptions('wing');
  const foilOptions = getAssetOptions('foil');
  const boardOptions = getAssetOptions('board');
  
  // Extract date and time for dropdowns
  const getInitialDateTime = () => {
    // Ensure dates are Date objects, not strings
    let startDate = booking?.start ? new Date(booking.start) : new Date();
    
    // Use the booking's actual end time if it exists, otherwise calculate default
    let endDate = booking?.end ? new Date(booking.end) : new Date(startDate.getTime() + MIN_BOOKING_DURATION * 60000);
    
    // Ensure start time is within valid range
    const startHour = startDate.getHours();
    const startMinute = startDate.getMinutes();
    
    if (startHour < OPEN_HOUR) {
      // Before opening hour - set to opening time
      startDate.setHours(OPEN_HOUR);
      startDate.setMinutes(0);
      
      // Only recalculate end time for new bookings (when no booking.end exists)
      if (!booking?.end) {
        endDate = new Date(startDate.getTime() + MIN_BOOKING_DURATION * 60000);
      }
    } else if (startHour > LAST_START_HOUR || 
              (startHour === LAST_START_HOUR && startMinute > LAST_START_MINUTE)) {
      // After last start time - set to last valid start time
      startDate.setHours(LAST_START_HOUR);
      startDate.setMinutes(LAST_START_MINUTE);
      
      // Only recalculate end time for new bookings
      if (!booking?.end) {
        endDate = new Date(startDate.getTime() + MIN_BOOKING_DURATION * 60000);
      }
    }
    
    // Ensure end time is not after closing - apply this validation to all cases
    if (endDate.getHours() > CLOSE_HOUR || 
       (endDate.getHours() === CLOSE_HOUR && endDate.getMinutes() > 0)) {
      endDate.setHours(CLOSE_HOUR);
      endDate.setMinutes(0);
    }
    
    return {
      startDate: formatDateForInput(startDate),
      startHour: formatHourForDropdown(startDate),
      startMinute: formatMinuteForDropdown(startDate),
      endDate: formatDateForInput(endDate),
      endHour: formatHourForDropdown(endDate),
      endMinute: formatMinuteForDropdown(endDate)
    };
  };
  
  const initialDateTime = getInitialDateTime();
  
  const [formData, setFormData] = useState({
    name: user?.name || booking?.name || booking?.title || '',
    // Extract asset IDs from extendedProps if available, otherwise use direct properties
    // Handle both string IDs and object references, with null checks
    wingId: (typeof booking?.extendedProps?.wingId === 'object' && booking.extendedProps.wingId ? booking.extendedProps.wingId._id : booking?.extendedProps?.wingId) || booking?.wingId || '',
    foilId: (typeof booking?.extendedProps?.foilId === 'object' && booking.extendedProps.foilId ? booking.extendedProps.foilId._id : booking?.extendedProps?.foilId) || booking?.foilId || '',
    boardId: (typeof booking?.extendedProps?.boardId === 'object' && booking.extendedProps.boardId ? booking.extendedProps.boardId._id : booking?.extendedProps?.boardId) || booking?.boardId || '',
    startDate: initialDateTime.startDate,
    startHour: initialDateTime.startHour,
    startMinute: initialDateTime.startMinute,
    endDate: initialDateTime.endDate,
    endHour: initialDateTime.endHour,
    endMinute: initialDateTime.endMinute
  });
  
  // Add useEffect to update form when booking prop changes
  useEffect(() => {
    if (booking) {
      console.log('Updating form data from booking:', booking);
      setFormData(prev => ({
        ...prev,
        name: booking.name || booking.title || prev.name,
        // Handle both string IDs and object references, with null checks
        wingId: (typeof booking?.extendedProps?.wingId === 'object' && booking.extendedProps.wingId ? booking.extendedProps.wingId._id : booking?.extendedProps?.wingId) || booking?.wingId || prev.wingId,
        foilId: (typeof booking?.extendedProps?.foilId === 'object' && booking.extendedProps.foilId ? booking.extendedProps.foilId._id : booking?.extendedProps?.foilId) || booking?.foilId || prev.foilId,
        boardId: (typeof booking?.extendedProps?.boardId === 'object' && booking.extendedProps.boardId ? booking.extendedProps.boardId._id : booking?.extendedProps?.boardId) || booking?.boardId || prev.boardId,
      }));
    }
  }, [booking]);
  
  // Format helpers
  function formatDateForInput(date) {
    if (!(date instanceof Date)) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
  
  function formatHourForDropdown(date) {
    if (!(date instanceof Date)) return String(OPEN_HOUR).padStart(2, '0');
    const hours = date.getHours();
    return String(hours).padStart(2, '0');
  }
  
  function formatMinuteForDropdown(date) {
    if (!(date instanceof Date)) return '00';
    // Round to nearest increment
    const mins = date.getMinutes();
    const rounded = Math.round(mins / MINUTE_INCREMENTS) * MINUTE_INCREMENTS;
    return String(rounded === 60 ? 0 : rounded).padStart(2, '0');
  }
  
  // Generate minute options based on configuration
  const minuteOptions = [];
  for (let i = 0; i < 60; i += MINUTE_INCREMENTS) {
    const value = String(i).padStart(2, '0');
    minuteOptions.push({ value, label: value });
  }
  
  // Generate hour options for start times
  const startHourOptions = [];
  for (let hour = OPEN_HOUR; hour <= LAST_START_HOUR; hour++) {
    const value = String(hour).padStart(2, '0');
    startHourOptions.push({ value, label: value });
  }
  
  // Generate hour options for end times
  const endHourOptions = [];
  for (let hour = OPEN_HOUR; hour <= CLOSE_HOUR; hour++) {
    const value = String(hour).padStart(2, '0');
    endHourOptions.push({ value, label: value });
  }
  
  // Handle form input changes with improved time calculation
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Always sync end date with start date
      if (name === 'startDate') {
        updated.endDate = value;
      }
      
      // If start time changed, automatically set end time to start time + 2 hours
      if (name === 'startDate' || name === 'startHour' || name === 'startMinute') {
        try {
          // Create a start date object
          const startDateTime = new Date(
            `${updated.startDate}T${updated.startHour}:${updated.startMinute}:00`
          );
          
          // Calculate end time as start time + 2 hours
          const newEndDateTime = new Date(startDateTime.getTime() + 2 * 60 * 60 * 1000);
          
          // Ensure end time doesn't exceed closing time
          if (newEndDateTime.getHours() > CLOSE_HOUR || 
              (newEndDateTime.getHours() === CLOSE_HOUR && newEndDateTime.getMinutes() > 0)) {
            newEndDateTime.setHours(CLOSE_HOUR);
            newEndDateTime.setMinutes(0);
          }
          
          // Update end time fields (end date is already synced with start date)
          updated.endHour = formatHourForDropdown(newEndDateTime);
          updated.endMinute = formatMinuteForDropdown(newEndDateTime);
        } catch (error) {
          console.error('Error calculating end time:', error);
        }
      }
      
      // Extra validation for end time
      if (name === 'endHour' && Number(value) === CLOSE_HOUR) {
        // If closing hour is selected, force minutes to 00
        updated.endMinute = '00';
      }
      
      return updated;
    });
  };

  // Specific handler for date input that auto-closes the date picker
  const handleDateChange = (e) => {
    handleChange(e);
    // Auto-close the date picker by blurring the input
    setTimeout(() => {
      if (e.target) {
        e.target.blur();
      }
    }, 0);
  };

  // Function to check if an asset is available for a given time period
  const checkAssetAvailability = useCallback((assetId, assetType, startTime, endTime) => {
    // If asset is not available in general, return unavailable
    const asset = assets.find(a => a._id === assetId);
    if (!asset || !asset.available) {
      return { isAvailable: false, reason: 'unavailable' };
    }

    // If editing an existing booking, exclude it from conflict checking
    const currentBookingId = booking?.id;
    
    // Check all bookings for conflicts with this asset
    const allAssetBookings = [];
    Object.values(allBookings).forEach(assetBookings => {
      if (assetBookings) {
        allAssetBookings.push(...assetBookings);
      }
    });

    // Filter bookings that use this specific asset
    const conflictingBookings = allAssetBookings.filter(existingBooking => {
      // Skip if it's the same booking we're editing
      if (currentBookingId && existingBooking.id === currentBookingId) {
        return false;
      }
      
      // Check if this booking uses the asset we're checking
      const usesAsset = (assetType === 'wing' && existingBooking.wingId === assetId) ||
                       (assetType === 'foil' && existingBooking.foilId === assetId) ||
                       (assetType === 'board' && existingBooking.boardId === assetId) ||
                       // Also check extendedProps for backward compatibility
                       (assetType === 'wing' && existingBooking.extendedProps?.wingId === assetId) ||
                       (assetType === 'foil' && existingBooking.extendedProps?.foilId === assetId) ||
                       (assetType === 'board' && existingBooking.extendedProps?.boardId === assetId);
      
      if (!usesAsset) return false;

      // Check for time overlap
      const existingStart = new Date(existingBooking.start);
      const existingEnd = new Date(existingBooking.end);
      
      // Two bookings overlap if one starts before the other ends
      const hasOverlap = startTime < existingEnd && endTime > existingStart;
      
      return hasOverlap;
    });

    if (conflictingBookings.length > 0) {
      return { isAvailable: false, reason: 'booked' };
    }

    return { isAvailable: true, reason: null };
  }, [assets, allBookings, booking?.id]);

  // Memoized availability calculations that update when form data changes
  const assetAvailability = useMemo(() => {
    try {
      const startDateTime = new Date(`${formData.startDate}T${formData.startHour}:${formData.startMinute}:00`);
      const endDateTime = new Date(`${formData.endDate}T${formData.endHour}:${formData.endMinute}:00`);
      
      const wingAvailability = {};
      const foilAvailability = {};
      const boardAvailability = {};
      
      // Calculate availability for all wings
      wingOptions.forEach(wing => {
        wingAvailability[wing._id] = checkAssetAvailability(wing._id, 'wing', startDateTime, endDateTime);
      });
      
      // Calculate availability for all foils
      foilOptions.forEach(foil => {
        foilAvailability[foil._id] = checkAssetAvailability(foil._id, 'foil', startDateTime, endDateTime);
      });
      
      // Calculate availability for all boards
      boardOptions.forEach(board => {
        boardAvailability[board._id] = checkAssetAvailability(board._id, 'board', startDateTime, endDateTime);
      });
      
      return {
        wings: wingAvailability,
        foils: foilAvailability,
        boards: boardAvailability
      };
    } catch (error) {
      // If there's an error parsing dates, assume all available
      console.error('Error calculating asset availability:', error);
      return {
        wings: {},
        foils: {},
        boards: {}
      };
    }
  }, [formData.startDate, formData.startHour, formData.startMinute, formData.endDate, formData.endHour, formData.endMinute, checkAssetAvailability, wingOptions, foilOptions, boardOptions, allBookings]);

  // Helper function to get availability for a specific asset
  const getAssetAvailability = useCallback((assetId, assetType) => {
    switch (assetType) {
      case 'wing':
        return assetAvailability.wings[assetId] || { isAvailable: true, reason: null };
      case 'foil':
        return assetAvailability.foils[assetId] || { isAvailable: true, reason: null };
      case 'board':
        return assetAvailability.boards[assetId] || { isAvailable: true, reason: null };
      default:
        return { isAvailable: true, reason: null };
    }
  }, [assetAvailability]);

  // Memoized compatibility cache - only recalculates when selected assets change
  // Use a stable key to prevent unnecessary recalculations
  const selectedAssetsKey = useMemo(() => {
    const ids = [formData.wingId, formData.foilId, formData.boardId].filter(Boolean).sort();
    return ids.join('-');
  }, [formData.wingId, formData.foilId, formData.boardId]);

  const compatibilityCache = useMemo(() => {
    const selectedIds = [formData.wingId, formData.foilId, formData.boardId].filter(Boolean);
    
    const cache = new Map();

    // Pre-calculate compatibility for all assets against selected assets
    assets.forEach(asset => {
      if (!asset._id) return;

      // If no assets are selected, everything is compatible
      if (selectedIds.length === 0) {
        cache.set(asset._id, { isCompatible: true, reason: null });
        return;
      }

      let isCompatible = true;
      let incompatibleWith = null;

      // Check compatibility with each selected asset
      for (const selectedId of selectedIds) {
        if (selectedId === asset._id) continue; // Don't check against self

        let compatibility = null;

        // Check asset's compatibility matrix
        if (asset.compatibilityMatrix) {
          if (typeof asset.compatibilityMatrix.get === 'function') {
            compatibility = asset.compatibilityMatrix.get(selectedId);
          } else if (typeof asset.compatibilityMatrix === 'object') {
            compatibility = asset.compatibilityMatrix[selectedId];
          }
        }

        // If explicitly incompatible, mark as such
        if (compatibility === false) {
          isCompatible = false;
          incompatibleWith = selectedId;
          break;
        }

        // Also check reverse compatibility
        const selectedAsset = assets.find(a => a._id === selectedId);
        if (selectedAsset?.compatibilityMatrix) {
          let reverseCompatibility = null;

          if (typeof selectedAsset.compatibilityMatrix.get === 'function') {
            reverseCompatibility = selectedAsset.compatibilityMatrix.get(asset._id);
          } else if (typeof selectedAsset.compatibilityMatrix === 'object') {
            reverseCompatibility = selectedAsset.compatibilityMatrix[asset._id];
          }

          if (reverseCompatibility === false) {
            isCompatible = false;
            incompatibleWith = selectedId;
            break;
          }
        }
      }

      cache.set(asset._id, { isCompatible, reason: isCompatible ? null : 'incompatible' });
    });

    return cache;
  }, [selectedAssetsKey, assets]); // Only depend on the stable key, not individual IDs

  // Optimized compatibility checker using the cache
  const getAssetCompatibility = useCallback((assetId) => {
    return compatibilityCache.get(assetId) || { isCompatible: true, reason: null };
  }, [compatibilityCache]);
  
  // Handle form submission with additional validation
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Create Date objects from form data
    const startDateTime = new Date(`${formData.startDate}T${formData.startHour}:${formData.startMinute}:00`);
    const endDateTime = new Date(`${formData.endDate}T${formData.endHour}:${formData.endMinute}:00`);
    
    // Validate start time is within operating hours
    const startHour = startDateTime.getHours();
    const startMinute = startDateTime.getMinutes();
    
    if (startHour < OPEN_HOUR) {
      alert(`Start time must be after ${OPEN_HOUR}:00 AM`);
      return;
    }
    
    if (startHour > LAST_START_HOUR || 
        (startHour === LAST_START_HOUR && startMinute > LAST_START_MINUTE)) {
      alert(`Start time must be before ${LAST_START_HOUR}:${String(LAST_START_MINUTE).padStart(2,'0')}`);
      return;
    }
    
    // Validate end time is within operating hours
    if (endDateTime.getHours() > CLOSE_HOUR || 
        (endDateTime.getHours() === CLOSE_HOUR && endDateTime.getMinutes() > 0)) {
      alert(`End time cannot be later than ${CLOSE_HOUR}:00`);
      return;
    }
    
    // Check if booking meets minimum duration
    const diffInMinutes = (endDateTime - startDateTime) / 60000;
    if (diffInMinutes < MIN_BOOKING_DURATION) {
      alert(`Booking must be at least ${MIN_BOOKING_DURATION} minutes long`);
      return;
    }
    
    // Check if at least one piece of equipment is selected
    if (!formData.wingId && !formData.foilId && !formData.boardId) {
      alert('Please select at least one piece of equipment.');
      return;
    }
    
    // Handle the booking save/create
    const bookingData = {
      ...formData,
      id: booking?.id,
      start: startDateTime,
      end: endDateTime
    };
    
    try {
      console.log('📤 Sending booking update request...');
      if (mode === 'create') {
        await bookingService.create(bookingData);
        console.log('✅ Booking created successfully');
      } else {
        await bookingService.update(bookingData.id, {
          name: bookingData.name,
          wingId: bookingData.wingId || null,
          foilId: bookingData.foilId || null,
          boardId: bookingData.boardId || null,
          start: bookingData.start,
          end: bookingData.end
        });
        console.log('✅ Booking updated successfully');
      }
      console.log(' Closing modal...');
      onClose(); // Close the modal
    } catch (err) {
      console.error('❌ Booking operation failed:', err);
      alert(`Failed to ${mode === 'create' ? 'create' : 'save'} booking: ${err.error || err.message || 'Unknown error'}`);
    }
  };
  
  // Handle delete button
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this booking?')) {
      try {
        await bookingService.delete(booking.id);
        onClose(); // Close the modal
      } catch (err) {
        alert(`Failed to delete booking: ${err.message || 'Unknown error'}`);
      }
    }
  };
  
  return (
    <div className={`modal-overlay ${isMinimized ? 'minimized' : ''}`} onClick={isMinimized ? undefined : onClose}>
      <div 
        className={`modal-content ${isMinimized ? 'minimized' : ''}`} 
        onClick={e => {
          e.stopPropagation();
          // If minimized and clicked anywhere on the modal content, expand it
          if (isMinimized) {
            onExpand();
          }
        }}
      >
        <div className="modal-header">
          <h2>
            {mode === 'create' ? 'Book Equipment' : 
             mode === 'view' ? 'View Past Booking' : 'View Booking'}
          </h2>
          <div className="modal-controls">
            {/* Move booking ID to top right */}
            {(mode === 'edit' || mode === 'view') && booking?.id && !isMinimized && (
              <div className="booking-id">
                <span className="id-label">ID:</span>
                <span className="booking-id-value">
                  ...{booking.id.slice(-12)}
                </span>
              </div>
            )}
            <div className="modal-buttons">
              <button 
                type="button" 
                className="minimize-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isMinimized) {
                    onExpand();
                  } else {
                    onMinimize();
                  }
                }}
                title={isMinimized ? "Expand" : "Minimize"}
              >
                {isMinimized ? '□' : '_'}
              </button>
              <button 
                type="button" 
                className="close-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                title="Close"
              >
                ×
              </button>
            </div>
          </div>
        </div>
        
        {!isMinimized && (
          <form onSubmit={handleSubmit}>

          <div className="form-group">
            <label>Name</label>
            <input 
              type="text" 
              name="name" 
              value={formData.name} 
              disabled
              style={{ backgroundColor: '#eee', color: '#555', cursor: 'not-allowed' }}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Date</label>
            <div className="date-selectors">
              <input 
                type="date" 
                name="startDate" 
                value={formData.startDate} 
                onChange={handleDateChange}
                required
                disabled={isReadOnly}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Time</label>
            <div className="time-row">
              <div className="time-section">
                <span className="time-label">Start:</span>
                <div className="time-container">
                  <select 
                    name="startHour" 
                    value={formData.startHour} 
                    onChange={handleChange}
                    required
                    disabled={isReadOnly}
                  >
                    {startHourOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <span className="time-separator">:</span>
                  <select 
                    name="startMinute" 
                    value={formData.startMinute} 
                    onChange={handleChange}
                    required
                    disabled={isReadOnly || (Number(formData.startHour) === LAST_START_HOUR && LAST_START_MINUTE === 0)}
                  >
                    {minuteOptions.map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        disabled={Number(formData.startHour) === LAST_START_HOUR && Number(option.value) > LAST_START_MINUTE}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="time-section">
                <span className="time-label">End:</span>
                <div className="time-container">
                  <select 
                    name="endHour" 
                    value={formData.endHour} 
                    onChange={handleChange}
                    required
                    disabled={isReadOnly}
                  >
                    {endHourOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <span className="time-separator">:</span>
                  <select 
                    name="endMinute" 
                    value={formData.endMinute} 
                    onChange={handleChange}
                    required
                    disabled={isReadOnly || Number(formData.endHour) === CLOSE_HOUR}
                  >
                    {minuteOptions.map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        disabled={Number(formData.endHour) === CLOSE_HOUR && option.value !== '00'}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            {(Number(formData.startHour) === LAST_START_HOUR || Number(formData.endHour) === CLOSE_HOUR) && (
              <small className="help-text">
                {Number(formData.startHour) === LAST_START_HOUR && `Latest start: ${LAST_START_HOUR}:${String(LAST_START_MINUTE).padStart(2,'0')}. `}
                {Number(formData.endHour) === CLOSE_HOUR && `End at ${CLOSE_HOUR}:00 only.`}
              </small>
            )}
          </div>
          
          <div className="form-group">
            <label>Wing</label>
            <select 
              name="wingId" 
              value={formData.wingId} 
              onChange={handleChange}
              disabled={isReadOnly}
            >
              <option value="">None</option>
              {wingOptions.map(wing => {
                const availability = getAssetAvailability(wing._id, 'wing');
                const compatibility = getAssetCompatibility(wing._id);
                const isDisabled = isReadOnly || !availability.isAvailable || !compatibility.isCompatible;
                let displayText = wing.name;
                
                if (!availability.isAvailable) {
                  displayText += availability.reason === 'unavailable' ? ' - unavailable' : ' - booked';
                } else if (!compatibility.isCompatible) {
                  displayText += ' - incompatible';
                }
                
                return (
                  <option 
                    key={wing._id} 
                    value={wing._id} 
                    disabled={isDisabled}
                    style={(!availability.isAvailable || !compatibility.isCompatible) ? { color: '#999', backgroundColor: '#f5f5f5' } : {}}
                  >
                    {displayText}
                  </option>
                );
              })}
            </select>
          </div>
          
          <div className="form-group">
            <label>Foil</label>
            <select 
              name="foilId" 
              value={formData.foilId} 
              onChange={handleChange}
              disabled={isReadOnly}
            >
              <option value="">None</option>
              {foilOptions.map(foil => {
                const availability = getAssetAvailability(foil._id, 'foil');
                const compatibility = getAssetCompatibility(foil._id);
                const isDisabled = isReadOnly || !availability.isAvailable || !compatibility.isCompatible;
                let displayText = foil.name;
                
                if (!availability.isAvailable) {
                  displayText += availability.reason === 'unavailable' ? ' - unavailable' : ' - booked';
                } else if (!compatibility.isCompatible) {
                  displayText += ' - incompatible';
                }
                
                return (
                  <option 
                    key={foil._id} 
                    value={foil._id} 
                    disabled={isDisabled}
                    style={(!availability.isAvailable || !compatibility.isCompatible) ? { color: '#999', backgroundColor: '#f5f5f5' } : {}}
                  >
                    {displayText}
                  </option>
                );
              })}
            </select>
          </div>
          
          <div className="form-group">
            <label>Board</label>
            <select 
              name="boardId" 
              value={formData.boardId} 
              onChange={handleChange}
              disabled={isReadOnly}
            >
              <option value="">None</option>
              {boardOptions.map(board => {
                const availability = getAssetAvailability(board._id, 'board');
                const compatibility = getAssetCompatibility(board._id);
                const isDisabled = isReadOnly || !availability.isAvailable || !compatibility.isCompatible;
                let displayText = board.name;
                
                if (!availability.isAvailable) {
                  displayText += availability.reason === 'unavailable' ? ' - unavailable' : ' - booked';
                } else if (!compatibility.isCompatible) {
                  displayText += ' - incompatible';
                }
                
                return (
                  <option 
                    key={board._id} 
                    value={board._id} 
                    disabled={isDisabled}
                    style={(!availability.isAvailable || !compatibility.isCompatible) ? { color: '#999', backgroundColor: '#f5f5f5' } : {}}
                  >
                    {displayText}
                  </option>
                );
              })}
            </select>
          </div>
          
          <div className="form-actions">
            <button type="button" onClick={onClose}>
              {mode === 'view' ? 'Close' : 'Cancel'}
            </button>
            {mode === 'create' && (
              <button type="submit" className="primary">
                Create Booking
              </button>
            )}
            {mode === 'edit' && isAdminOrSelf && (
              <button type="submit" className="primary">
                Update Booking
              </button>
            )}
            {mode === 'edit' && isAdminOrSelf && (
              <button 
                type="button" 
                className="delete" 
                onClick={handleDelete}
              >
                Delete Booking
              </button>
            )}
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

export default BookingModal;